<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="姓名" prop="realName">
        <el-input
          v-model="queryParams.realName"
          class="!w-160px"
          clearable
          placeholder="联系人姓名"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="手机号" prop="mobile">
        <el-input
          v-model="queryParams.mobile"
          class="!w-160px"
          clearable
          placeholder="手机号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="意向类型" prop="agentType">
        <el-select v-model="queryParams.agentType" class="!w-160px" clearable placeholder="全部">
          <el-option
            v-for="item in AREA_AGENT_TYPE"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" class="!w-160px" clearable placeholder="全部">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.TRADE_AREA_AGENT_LEAD_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="提交时间" prop="createTime">
        <el-date-picker
          v-model="queryParams.createTime"
          class="!w-240px"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始"
          end-placeholder="结束"
          :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
        <el-button @click="resetQuery"><Icon class="mr-5px" icon="ep:refresh" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <el-table-column align="center" label="编号" prop="id" width="80" />
      <el-table-column align="left" label="提交用户" min-width="120">
        <template #default="{ row }">
          <div>ID：{{ row.userId }}</div>
          <div>{{ row.userNickname || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column align="left" label="联系方式" min-width="150">
        <template #default="{ row }">
          <div>{{ row.realName }}</div>
          <div>{{ row.mobile }}</div>
          <div v-if="row.companyName" class="text-xs text-gray-400">{{ row.companyName }}</div>
        </template>
      </el-table-column>
      <el-table-column align="center" label="意向类型" width="110">
        <template #default="{ row }">
          {{ row.agentTypeName || labelOf(AREA_AGENT_TYPE, row.agentType) || '-' }}
        </template>
      </el-table-column>
      <el-table-column align="left" label="意向辖区" min-width="160">
        <template #default="{ row }">{{ row.areaNames || '-' }}</template>
      </el-table-column>
      <el-table-column align="left" label="补充说明" min-width="160">
        <template #default="{ row }">{{ row.remark || '-' }}</template>
      </el-table-column>
      <el-table-column align="center" label="状态" width="110">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.TRADE_AREA_AGENT_LEAD_STATUS" :value="row.status" />
        </template>
      </el-table-column>
      <el-table-column align="left" label="跟进情况" min-width="160">
        <template #default="{ row }">
          <div v-if="row.followTime" class="text-xs text-gray-400">{{ row.followTime }}</div>
          <div v-if="row.followRemark">{{ row.followRemark }}</div>
          <div v-if="!row.followTime && !row.followRemark" class="text-xs text-gray-400">-</div>
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="提交时间"
        prop="createTime"
        :formatter="dateFormatter"
        width="170"
      />
      <el-table-column align="center" label="操作" fixed="right" width="90">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['trade:area-agent:lead:update']"
            link
            type="primary"
            @click="openFollow(row)"
          >
            跟进
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="followVisible" title="跟进意向线索" width="460px">
    <el-form label-width="80px">
      <el-form-item label="联系人">
        {{ followRow?.realName }} / {{ followRow?.mobile }}
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="followForm.status" class="!w-200px">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.TRADE_AREA_AGENT_LEAD_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="跟进备注">
        <el-input v-model="followForm.followRemark" type="textarea" :rows="3" maxlength="200" show-word-limit placeholder="沟通结果、后续安排" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button type="primary" :loading="followLoading" @click="submitFollow">确 定</el-button>
      <el-button @click="followVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import * as LeadApi from '@/api/mall/trade/areaAgent/lead'
import { AREA_AGENT_TYPE, labelOf } from '../constants'

defineOptions({ name: 'TradeAreaAgentLead' })

const message = useMessage()
const loading = ref(false)
const list = ref<LeadApi.AreaAgentLeadVO[]>([])
const total = ref(0)
const queryFormRef = ref()
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  realName: undefined as string | undefined,
  mobile: undefined as string | undefined,
  agentType: undefined as number | undefined,
  status: undefined as number | undefined,
  createTime: undefined as string[] | undefined
})

const followVisible = ref(false)
const followLoading = ref(false)
const followRow = ref<LeadApi.AreaAgentLeadVO>()
const followForm = reactive({
  status: 0 as number,
  followRemark: ''
})

const getList = async () => {
  loading.value = true
  try {
    const data = await LeadApi.getAreaAgentLeadPage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const openFollow = (row: LeadApi.AreaAgentLeadVO) => {
  followRow.value = row
  followForm.status = row.status ?? 0
  followForm.followRemark = row.followRemark || ''
  followVisible.value = true
}

const submitFollow = async () => {
  if (!followRow.value?.id) return
  followLoading.value = true
  try {
    await LeadApi.updateAreaAgentLeadStatus({
      id: followRow.value.id,
      status: followForm.status,
      followRemark: followForm.followRemark
    })
    message.success('已保存跟进')
    followVisible.value = false
    await getList()
  } finally {
    followLoading.value = false
  }
}

onMounted(getList)
</script>
